// @ts-check
// Seletor de modelo de e-mail para o passo "Enviar e-mail" dos fluxos (spec/email.md seção 19.9; frente 2D usa): modelos
// não arquivados com miniatura, busca, chips de finalidade, "Criar modelo" (abre o editor numa rota nova com ?from= e
// volta com ?emailTemplateId=, K20) e "Usar um modelo pronto" (galeria, cria a cópia e já escolhe). Mostra os avisos do
// modelo: com erro e sem versão válida. Devolve { templateId, name, purpose }.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { createEmailTemplate, listEmailTemplates } from '../../data/email-templates.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { useDebouncedValue } from '../../ui/hooks.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { EmailDialog } from '../email-design/email-dialog.js';
import { EmailTemplateThumbnail } from '../email-design/email-thumbnail.js';
import { EmailGalleryGrid } from '../email-design/template-create-dialog.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/email-templates.js').EmailTemplateSummary} EmailTemplateSummary
 * @typedef {{ templateId: string, name: string, purpose: 'MARKETING' | 'SERVICE' }} PickedEmailTemplate
 */

/** Caminho do "Criar modelo" com a volta. @param {string} returnPath */
export const newEmailTemplatePath = (returnPath) => `/campaigns/templates/email/new?from=${encodeURIComponent(returnPath)}`;

/** Avisos de um modelo para quem escolhe. @param {EmailTemplateSummary} template */
export function emailTemplateNotices(template) {
  /** @type {string[]} */
  const notices = [];
  if (template.hasErrors) notices.push(t('emailTemplates.picker.hasErrors'));
  else if (!template.hasValidContent) notices.push(t('emailTemplates.picker.noValidVersion'));
  return notices;
}

/**
 * Lista para escolher (embutida no painel do passo).
 * @param {{ value: string | null, onChange: (picked: PickedEmailTemplate) => void, returnPath: string, disabled?: boolean }} props
 */
export function EmailTemplatePicker({ value, onChange, returnPath, disabled = false }) {
  const [templates, setTemplates] = useState(/** @type {EmailTemplateSummary[] | null} */ (null));
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [purpose, setPurpose] = useState(/** @type {'' | 'MARKETING' | 'SERVICE'} */ (''));
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(/** @type {string | null} */ (null));

  const load = () => {
    setFailed(false);
    listEmailTemplates({ search: debouncedSearch || undefined, purpose: purpose || undefined })
      .then(setTemplates)
      .catch(() => setFailed(true));
  };
  useEffect(load, [debouncedSearch, purpose]);

  /** @param {import('../../data/email-templates.js').EmailGalleryItem} item */
  const useGalleryItem = async (item) => {
    setBusyKey(item.key);
    try {
      const created = await createEmailTemplate({ galleryKey: item.key });
      setIsGalleryOpen(false);
      onChange({ templateId: created.id, name: created.name, purpose: created.purpose });
      load();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setBusyKey(null);
    }
  };

  return html`<div class="email-template-picker" data-testid="email-template-picker">
    <div class="email-template-picker-toolbar">
      <label class="email-search-field">
        <${Icon} name="IconSearch" size=${14} />
        <input type="search" value=${search} placeholder=${t('emailTemplates.search')} aria-label=${t('emailTemplates.search')}
          onInput=${(/** @type {Event} */ event) => setSearch(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      </label>
      <div class="email-choice-field" role="radiogroup" aria-label=${t('emailTemplates.picker.purpose')}>
        ${(/** @type {const} */ (['', 'MARKETING', 'SERVICE'])).map((option) => html`<button type="button" role="radio" class="email-choice-option"
          aria-checked=${purpose === option ? 'true' : 'false'} onClick=${() => setPurpose(option)}>
          <span>${option ? t(`emailTemplates.purposes.${option}`) : t('emailTemplates.picker.allPurposes')}</span>
        </button>`)}
      </div>
    </div>
    <div class="email-button-row">
      <${Button} title=${t('emailTemplates.picker.create')} icon="IconPlus" size="small" variant="secondary" disabled=${disabled} onClick=${() => navigate(newEmailTemplatePath(returnPath))} />
      <${Button} title=${t('emailTemplates.picker.useGallery')} icon="IconLayoutGrid" size="small" variant="secondary" disabled=${disabled} onClick=${() => setIsGalleryOpen(true)} />
    </div>
    ${failed
      ? html`<div class="email-inline-error" role="alert"><span>${t('emailTemplates.loadError')}</span>
          <${Button} title=${t('emailTemplates.retry')} size="small" variant="secondary" onClick=${load} /></div>`
      : templates === null
        ? html`<div class="email-template-picker-list">${Array.from({ length: 3 }, (_, index) => html`<${Skeleton} key=${index} height=${64} radius=${6} />`)}</div>`
        : templates.length === 0
          ? html`<${EmptyState} icon="IconMail" title=${debouncedSearch ? t('emailTemplates.noResults', { search: debouncedSearch }) : t('emailTemplates.emptyTitle')} />`
          : html`<ul class="email-template-picker-list" role="listbox" aria-label=${t('emailTemplates.picker.title')}>
              ${templates.map((template) => {
                const notices = emailTemplateNotices(template);
                return html`<li key=${template.id}>
                  <button type="button" role="option" class="email-template-picker-item" aria-selected=${value === template.id ? 'true' : 'false'} disabled=${disabled}
                    onClick=${() => onChange({ templateId: template.id, name: template.name, purpose: template.purpose })}>
                    <${EmailTemplateThumbnail} templateId=${template.id} updatedAt=${template.updatedAt} title=${template.name} />
                    <span class="email-template-picker-texts">
                      <span class="email-template-card-name">${template.name}</span>
                      <span class="email-chip-row">
                        <span class="email-chip">${t(`emailTemplates.categories.${template.category}`)}</span>
                        ${template.purpose === 'SERVICE' ? html`<span class="email-chip" data-tone="blue">${t('emailTemplates.card.service')}</span>` : null}
                      </span>
                      ${notices.map((notice) => html`<span class="input-hint" data-danger="true"><${Icon} name="IconAlertTriangle" size=${12} /> ${notice}</span>`)}
                    </span>
                    ${value === template.id ? html`<${Icon} name="IconCheck" size=${16} className="email-template-picker-check" />` : null}
                  </button>
                </li>`;
              })}
            </ul>`}
    ${isGalleryOpen
      ? html`<${EmailDialog} title=${t('emailTemplates.gallery.title')} onClose=${() => setIsGalleryOpen(false)} size="large">
          <${EmailGalleryGrid} busyKey=${busyKey} onUse=${useGalleryItem} />
        <//>`
      : null}
  </div>`;
}

/**
 * O seletor num diálogo.
 * @param {{ value: string | null, onChange: (picked: PickedEmailTemplate) => void, onClose: () => void, returnPath: string }} props
 */
export function EmailTemplatePickerDialog({ value, onChange, onClose, returnPath }) {
  return html`<${EmailDialog} title=${t('emailTemplates.picker.title')} onClose=${onClose} size="large">
    <${EmailTemplatePicker} value=${value} returnPath=${returnPath}
      onChange=${(/** @type {PickedEmailTemplate} */ picked) => {
        onChange(picked);
        onClose();
      }} />
  <//>`;
}
